import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Radio, RefreshCw } from "lucide-react";
import axios, { API, fmt, fmtTime, STRATEGY_LABELS } from "./commoditiesLib";
import EmaSignalLog from "./EmaSignalLog";

const SignalCard = ({ s }) => (
  <div className="border border-grid p-4 space-y-2" data-testid="commodities-signal-card">
    <div className="flex items-center justify-between">
      <span className="font-heading text-base">{s.underlying}</span>
      <span
        className="px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider border"
        style={{
          color: s.direction === "Bullish" ? "var(--bull)" : "var(--bear)",
          borderColor: s.direction === "Bullish" ? "var(--bull)" : "var(--bear)",
        }}
      >
        {s.direction}
      </span>
    </div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
      {STRATEGY_LABELS[s.strategy] || s.strategy}
      {s._already_seen && <span className="ml-2 text-muted-3 normal-case">(already acted on)</span>}
    </div>
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono-data text-muted-2">
      <div>Leg <span className="text-muted-1">{s.option_type} {fmt(s.strike, 0)}</span></div>
      <div>Premium <span className="text-muted-1">{fmt(s.ltp)}</span></div>
      <div>Target <span className="text-muted-1">{fmt(s.target_high_premium)}</span></div>
      <div>RVOL <span className="text-muted-1">{s.rvol != null ? `${fmt(s.rvol, 2)}x` : "—"}</span></div>
      <div>IV <span className="text-muted-1">{s.iv != null ? `${fmt(s.iv * 100, 0)}%` : "—"}</span></div>
      <div>DTE <span className="text-muted-1">{s.dte_days != null ? `${fmt(s.dte_days, 0)}d` : "—"}</span></div>
    </div>
    <div className="text-[10px] text-muted-3 font-mono-data pt-1 border-t border-grid/40">
      Bar: {fmtTime(s.bar_time || s.signal_time)} · Simulated (Black-76)
    </div>
  </div>
);

const CommoditiesSignals = () => {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/commodities/signals`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load signals");
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const scanNow = async () => {
    setScanning(true);
    try {
      await axios.post(`${API}/commodities/scan`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const signals = data?.signals || [];

  return (
    <section className="space-y-4" data-testid="commodities-signals-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            Commodities · MCX
          </div>
          <h2 className="font-heading text-2xl flex items-center gap-2">
            <Radio size={20} /> Signals
          </h2>
          {data?.ran_at_utc && (
            <div className="text-[10px] text-muted-3 font-mono-data mt-1">
              Last scan: {fmtTime(data.ran_at_utc)}
            </div>
          )}
        </div>
        <button
          onClick={scanNow}
          disabled={scanning}
          data-testid="commodities-signals-scan-now"
          className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data flex items-center gap-2 disabled:opacity-50"
          style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
        >
          <RefreshCw size={12} className={scanning ? "animate-spin" : ""} /> Scan now
        </button>
      </div>

      {!data ? (
        <div className="text-muted-2 font-mono-data text-xs">Loading…</div>
      ) : signals.length === 0 ? (
        <div className="border border-grid p-8 text-center text-xs text-muted-2 font-mono-data">
          No confirmed signals on the most recent scan.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {signals.map((s, i) => (
            <SignalCard key={`${s.underlying}-${s.strategy}-${i}`} s={s} />
          ))}
        </div>
      )}

      <EmaSignalLog />
    </section>
  );
};

export default CommoditiesSignals;
